import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../utils/cx.js";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  extra?: ReactNode;
  size?: "default" | "small";
  variant?: "borderless" | "outlined";
  children: ReactNode;
}

/**
 * Surface container. Keeps the antd-era prop surface (title/extra/size/variant)
 * but renders a shadcn-style card: rounded-xl, hairline border, soft shadow.
 */
export function Card({
  title,
  extra,
  size = "default",
  variant = "outlined",
  className,
  style,
  id,
  onClick,
  children,
  ...rest
}: CardProps) {
  const pad = size === "small" ? "px-4 py-3" : "px-6 py-5";
  return (
    <div
      data-slot="card"
      id={id}
      onClick={onClick}
      style={style}
      className={cn(
        // Light hairline border (base sets the color) + a soft shadow from .az-card
        // (explicit CSS — Tailwind v4's shadow utilities render transparent here).
        "bg-card text-card-foreground flex flex-col rounded-xl",
        variant === "borderless" ? "" : "az-card",
        className,
      )}
      {...rest}
    >
      {(title != null || extra != null) && (
        <div className={cn("flex items-center justify-between gap-3 border-b", pad)}>
          {title != null && <div className="text-base font-semibold leading-none">{title}</div>}
          {extra != null && <div className="text-muted-foreground text-sm">{extra}</div>}
        </div>
      )}
      <div className={pad}>{children}</div>
    </div>
  );
}
